import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  debounce,
  CircularProgress,
  List,
  ListItem,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Tabs,
  Tab,
  Button,
  Typography,
  Tooltip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import {
  ApplicationUser,
  Customer,
  Unit,
} from "../QueueBoard/models/workOrderTypes";
import {
  searchCustomerListStyle,
  searchListItem,
  searchUnitListStyle,
} from "../QueueBoard/models/workOrderStyles";
import { formatDate2 } from "../common/Utils/commonUtilityFunctions";
import { useWorkOrderStore } from "../store/zustandWorkOrderStore";
import { UnitsAPI, UnitSearchField } from "../QueueBoard/api/unitApi";

const UNIT_SEARCH_PAGE_SIZE = 10;
import Swal from "sweetalert2";
import { CustomerAPI } from "../QueueBoard/api/customerApi";
import InfoIcon from "@mui/icons-material/Info";
import CustomerDetailDialog from "./customer/CustomerDetailDialog";

import packageJson from "../../../package.json";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { User } from "components/authentication/models/authTypes";
import localStorageHelper from "../../app/localStorage";
import { SettingsLocation } from "models/lookupTypes";
import NewCustomerDialog from "./customer/NewCustomerDialog";

type UnitFormValues = {
  unitIdentity?: number | null;
  woUnitIdentity?: number | null;
  woUnitBaseSerial?: string;
  model?: string;
  stockNumber?: string;
};

const getSelectedUnitIdentity = (values: UnitFormValues) =>
  values.unitIdentity ?? values.woUnitIdentity ?? null;

const WorkOrderDialog = ({
  isHistoryExists,
  formik,
  onUnitSearchStart,
  onUnitSearchEnd,
}) => {
  const {
    woStatuses,
    technicians,
    meterTypes,
    woPriorityList,
    locations,
    salesPersons,
  } = useWorkOrderStore();
  const navigate = useNavigate();
  //console.log(formik);
  const [units, setUnits] = useState<Unit[]>([]);
  const serialSearchRef = useRef<HTMLInputElement>(null);
  const modelSearchRef = useRef<HTMLInputElement>(null);
  const unitNumberSearchRef = useRef<HTMLInputElement>(null);
  const [activeUnitSearchField, setActiveUnitSearchField] =
    useState<UnitSearchField | null>(null);
  const [localMeterValue, setLocalMeterValue] = useState<string>("0");

  // Add state for dropdown visibility
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [unitHasMore, setUnitHasMore] = useState(false);
  const [unitLoadingMore, setUnitLoadingMore] = useState(false);
  const unitSearchPageRef = useRef(1);
  const unitSearchContextRef = useRef<{
    query: string;
    locationIdentity: number;
    customerIdentity: number;
    searchField?: UnitSearchField;
  } | null>(null);

  // Add refs for click outside detection
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const unitDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const value = formik.values.meterValue;
    if (value !== undefined && value !== null) {
      setLocalMeterValue(value.toString());
    }
  }, [formik.values.meterValue]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        target.closest(".MuiDialogActions-root") ||
        target.closest(".wo-dialog-form button")
      ) {
        return;
      }

      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCustomerDropdown(false);
        // Clear customer field if no selection was made
        if (!formik.values.customerIdentity && formik.values.customerName) {
          formik.setFieldValue("customerName", "");
        }
      }

      if (
        unitDropdownRef.current &&
        !unitDropdownRef.current.contains(event.target as Node)
      ) {
        setShowUnitDropdown(false);
        if (!getSelectedUnitIdentity(formik.values)) {
          if (formik.values.woUnitBaseSerial) {
            formik.setFieldValue("woUnitBaseSerial", "");
          }
          if (formik.values.model) {
            formik.setFieldValue("model", "");
          }
          if (formik.values.stockNumber) {
            formik.setFieldValue("stockNumber", "");
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    formik.values.customerIdentity,
    formik.values.customerName,
    formik.values.unitIdentity,
    formik.values.woUnitIdentity,
    formik.values.woUnitBaseSerial,
    formik.values.model,
    formik.values.stockNumber,
  ]);

  const [loading, setLoading] = useState(false);
  const [unitSearchLoading, setUnitSearchLoading] = useState(false);
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user"),
  );
  const formatUnitDropdownLabel = (unit: Unit) => {
    const serial = unit.baseSerial?.trim() || "";
    const model = unit.model?.trim() || "";
    const statusSuffix =
      unit.stockstatus?.trim() === "customer" ? ` - ${unit.stockstatus}` : "";

    if (serial && model) {
      return `${serial} - ${model}${statusSuffix}`;
    }
    if (serial) {
      return `${serial}${statusSuffix}`;
    }
    if (model) {
      return `${model}${statusSuffix}`;
    }
    return `${unit.unitNumber}${statusSuffix}`;
  };

  const clearUnitFields = async (formikInstance: typeof formik) => {
    await formikInstance.setFieldValue("unitIdentity", null);
    await formikInstance.setFieldValue("woUnitIdentity", null);
    await formikInstance.setFieldValue("stockNumber", "");
    await formikInstance.setFieldValue("model", "");
    await formikInstance.setFieldValue("woUnitBaseSerial", "");
    await formikInstance.setFieldValue("woUnitTag", "");
    await formikInstance.setFieldValue("woUnitDescription", "");
    await formikInstance.setFieldValue("woUnitIsAttachment", false);
    await formikInstance.setFieldValue("woUnitIsNew", false);
    await formikInstance.setFieldValue("warrantyDate", "");
  };

  // Function to fetch Units from the API
  const fetchUnits = async (
    query: string,
    locationIdentity: number,
    customerIdentity: number,
    searchField?: UnitSearchField,
    pageNumber = 1,
    append = false,
  ) => {
    if (append) {
      setUnitLoadingMore(true);
    } else {
      setUnitSearchLoading(true);
      unitSearchPageRef.current = 1;
      onUnitSearchStart();
    }

    unitSearchContextRef.current = {
      query,
      locationIdentity,
      customerIdentity,
      searchField,
    };

    try {
      const response = await UnitsAPI.GetUnitBySearchTerm(
        query,
        locationIdentity,
        customerIdentity,
        searchField,
        pageNumber,
        UNIT_SEARCH_PAGE_SIZE,
      );

      if (response === null) {
        if (!append) {
          setUnits([]);
          setShowUnitDropdown(false);
        }
        setUnitHasMore(false);
        return;
      }

      setUnits((prev) =>
        append ? [...prev, ...response.units] : response.units,
      );
      setUnitHasMore(response.hasMore);
      unitSearchPageRef.current = pageNumber;
      setShowUnitDropdown(response.units.length > 0 || append);
    } catch (error) {
      console.error("Error fetching Units:", error);
    } finally {
      if (append) {
        setUnitLoadingMore(false);
      } else {
        setUnitSearchLoading(false);
        onUnitSearchEnd();

        logAmplitudeEventWithProps(
          user.userNameAndCompany,
          {
            company: user.company,
            user: user.userName,
          },
          "fetchUnits",
          {
            elapsed_ms: "124",
            title: "Wo Detail Dialog",
            componentName: "WorkOrderFormDialog.tsx",
            version: packageJson.version,
          },
        );
      }
    }
  };

  const loadMoreUnits = () => {
    const context = unitSearchContextRef.current;
    if (
      !context ||
      !unitHasMore ||
      unitLoadingMore ||
      unitSearchLoading
    ) {
      return;
    }

    fetchUnits(
      context.query,
      context.locationIdentity,
      context.customerIdentity,
      context.searchField,
      unitSearchPageRef.current + 1,
      true,
    );
  };

  const handleUnitListScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 12) {
      loadMoreUnits();
    }
  };
  const useDynamicDebounce = (callback, delay) => {
    return useCallback(debounce(callback, delay), [delay]);
  };

  const debouncedFetchUnits = useDynamicDebounce(
    debounce(
      (
        query: string,
        locationIdentity: number,
        customerIdentity: number,
        searchField?: UnitSearchField,
      ) => {
        if (query?.length >= 3) {
          fetchUnits(query, locationIdentity, customerIdentity, searchField);
        } else {
          setUnits([]);
          setShowUnitDropdown(false);
          setUnitHasMore(false);
          unitSearchPageRef.current = 1;
          unitSearchContextRef.current = null;
        }
      },
      500,
    ),
    [],
  );

  const getUnitSearchFormikField = (searchField: UnitSearchField): string => {
    switch (searchField) {
      case "serial":
        return "woUnitBaseSerial";
      case "model":
        return "model";
      case "unitNumber":
        return "stockNumber";
    }
  };

  const handleUnitSearchChange = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    formikInstance: typeof formik,
    searchField: UnitSearchField,
  ) => {
    const { locationIdentity, customerIdentity } = formikInstance.values;

    if (!locationIdentity || !customerIdentity) {
      const missingField = !locationIdentity ? "location" : "customer";

      Swal.fire({
        title: "Error",
        html: `Please select the ${missingField} first`,
        icon: "error",
        confirmButtonColor: "#2371E7",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        customClass: {
          popup: "swal2-custom-zindex",
        },
      });

      return;
    }

    const query = event.target.value;
    const formikField = getUnitSearchFormikField(searchField);
    setActiveUnitSearchField(searchField);

    if (query === "") {
      await clearUnitFields(formikInstance);
      setUnits([]);
      setShowUnitDropdown(false);
      setUnitHasMore(false);
      unitSearchPageRef.current = 1;
      unitSearchContextRef.current = null;
      setActiveUnitSearchField(null);
      return;
    }

    const currentValue = formikInstance.values[formikField];
    if (currentValue === query) return;

    await formikInstance.setFieldValue("unitIdentity", null);
    await formikInstance.setFieldValue("woUnitIdentity", null);
    await formikInstance.setFieldValue(formikField, query);

    if (query.length >= 3) {
      setShowUnitDropdown(true);
    } else {
      setShowUnitDropdown(false);
    }

    debouncedFetchUnits(
      query,
      formikInstance.values.locationIdentity,
      formikInstance.values.customerIdentity,
      searchField,
    );
  };

  const handleUnitSelect = async (unit: Unit, formik) => {
    //console.log("before", formik.values.locationIdentity);
    await setFormikFieldValue("unitIdentity", unit.identity);
    await setFormikFieldValue("woUnitIdentity", unit.identity);
    await setFormikFieldValue("stockNumber", unit.unitNumber);
    setUnits([]);
    setShowUnitDropdown(false);
    setUnitHasMore(false);
    unitSearchPageRef.current = 1;
    unitSearchContextRef.current = null;
    await setFormikFieldValue("model", unit.model);
    await setFormikFieldValue("woUnitBaseSerial", unit.baseSerial);
    await setFormikFieldValue("woUnitTag", unit.tag);
    await setFormikFieldValue("woUnitDescription", unit.description);
    await setFormikFieldValue("woUnitIsAttachment", unit.isAttachment);
    await setFormikFieldValue("woUnitIsNew", unit.isNew);
    await setFormikFieldValue("warrantyDate", unit.warrantyDate);
    if (unit.meterType.length > 0) {
      await setFormikFieldValue("meterType", unit.meterType);
      await setFormikFieldValue("meterValue", unit.meterValue);
    }
    if (formik.onUnitSelect) {
      formik.onUnitSelect(unit);
    }
    await formik.validateForm();
    setActiveUnitSearchField(null);

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleUnitSelect",
      {
        elapsed_ms: "124",
        title: "Wo Detail Dialog",
        componentName: "WorkOrderFormDialog.tsx",
        version: packageJson.version,
      },
    );
  };

  const setFormikFieldValue = async (fieldName: string, fieldValue: any) => {
    await formik.setFieldValue(fieldName, fieldValue, true);
    await formik.setFieldTouched(fieldName, true, true);
    await formik.validateForm(); // Validate after setting all fields
  };

  function setLocationFormikFieldValue(fieldName: string, fieldValue: any) {
    setFormikFieldValue("locationIdentity", fieldValue);

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "woLocationChangeHandler",
      {
        elapsed_ms: "124",
        title: "Wo Detail Dialog",
        componentName: "WorkOrderFormDialog.tsx",
        version: packageJson.version,
      },
    );
  }
  async function setSalesPersonFormikFieldValue(
    identity: number,
    name: string,
  ) {
    await setFormikFieldValue("salesPersonIdentity", identity);
    await setFormikFieldValue("salesPersonName", name);
    formik.validateField("salesPersonIdentity").then((error) => {
      //console.log(error);
    });
  }
  const handleWoPriorityChange = (event, formik) => {
    formik.setFieldValue("woPriorityIdentity", event.target.value);

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleWoPriorityChange",
      {
        elapsed_ms: "124",
        title: "Wo Detail Dialog",
        componentName: "WorkOrderFormDialog.tsx",
        version: packageJson.version,
      },
    );
  };

  function setTechFormikFieldValue(fieldName: string, fieldValue: any) {
    formik.setFieldValue("technicianIdentity", fieldValue);
    //dispatch(setCurrentWorkOrderTech(fieldValue));
  }
  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, value || "");

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      name + "handleDateChange",
      {
        elapsed_ms: "124",
        title: "Wo Detail Dialog",
        componentName: "WorkOrderFormDialog.tsx",
        version: packageJson.version,
      },
    );
  };

  const handleDateTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (!value) {
      formik.setFieldValue(name, null);
      return;
    }
    // Set the formik field value directly to the string from the input
    formik.setFieldValue(name, value);
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      name + "handleDateTimeChange",
      {
        elapsed_ms: "124",
        title: "Wo Detail Dialog",
        componentName: "WorkOrderFormDialog.tsx",
        version: packageJson.version,
      },
    );
  };

  const handleUnitSearchOnFocus = (
    formikInstance: typeof formik,
    searchField: UnitSearchField,
  ) => {
    const { customerIdentity, locationIdentity } = formikInstance.values;
    const formikField = getUnitSearchFormikField(searchField);
    const fieldValue = formikInstance.values[formikField];

    setActiveUnitSearchField(searchField);

    if (customerIdentity && !fieldValue?.length) {
      setUnits([]);
      fetchUnits("", locationIdentity, customerIdentity, searchField);
    }

    if (units.length > 0) {
      setShowUnitDropdown(true);
    }
  };

  const renderUnitSearchDropdown = (searchField: UnitSearchField) =>
    showUnitDropdown &&
    units.length > 0 &&
    activeUnitSearchField === searchField ? (
      <List onScroll={handleUnitListScroll} sx={searchUnitListStyle(theme)}>
        {units.map((unit) => (
          <ListItem
            sx={searchListItem(theme)}
            key={unit.identity}
            onClick={() => handleUnitSelect(unit, formik)}
          >
            {formatUnitDropdownLabel(unit)}
          </ListItem>
        ))}
        {unitLoadingMore && (
          <ListItem sx={{ justifyContent: "center", py: 1 }}>
            <CircularProgress size={18} />
          </ListItem>
        )}
      </List>
    ) : null;

  const [customers, setCustomers] = useState<Customer[] | null>([]);
  // Function to fetch customers from the API
  const fetchCustomers = async (query) => {
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "customerSearch",
      {
        elapsed_ms: "124",
        title: "Wo Detail Dialog",
        componentName: "WorkOrderFormDialog.tsx",
        version: packageJson.version,
      },
    );

    setLoading(true);
    const customers = await CustomerAPI.searchCustomersByName(query);
    if (customers) {
      setCustomers(customers);
      // Show dropdown if there are results
      if (customers.length > 0) {
        setShowCustomerDropdown(true);
      } else {
        setShowCustomerDropdown(false);
      }
    }
    setLoading(false);
  };

  // Debounced function to delay the API call
  const debouncedFetchCustomers = useCallback(
    debounce((query) => {
      if (query.length >= 3) {
        fetchCustomers(query);
      } else {
        setCustomers([]); // Clear the list if query is less than 3 characters
        setShowCustomerDropdown(false); // Hide dropdown when clearing results
      }
    }, 500), // Adjust the debounce delay (500ms in this case)
    [],
  );
  const handleCustomerSelect = async (customer: Customer, formik) => {
    await formik.setFieldValue("customerIdentity", customer.identity);
    await formik.setFieldValue("customerName", customer.name);
    await formik.setFieldValue("creditHold", customer.creditHold);

    //Customer additional info
    await formik.setFieldValue("customerPhone", customer.customerPhone);
    await formik.setFieldValue("customerEmail", customer.customerEmail);
    await formik.setFieldValue("customerAddress", customer.customerAddress);

    setCustomers([]); // Clear the dropdown after selection
    setShowCustomerDropdown(false); // Hide the dropdown

    if (formik.onCustomerSelect) {
      formik.onCustomerSelect(customer);
    }
    formik.setFieldTouched("customerIdentity", true); // Mark as touched
    formik.setFieldTouched("customerName", true); // Mark name as touched

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "customerSelection",
      {
        elapsed_ms: "124",
        title: "Wo Detail Dialog",
        componentName: "WorkOrderFormDialog.tsx",
        version: packageJson.version,
      },
    );
  };
  const handleCustomerSearchChange = async (event, formik) => {
    const query = event.target.value;
    await formik.setFieldValue("customerIdentity", null);
    await formik.setFieldValue("customerName", query);

    await formik.setFieldValue("customerIdentity", null);
    await formik.setFieldValue("customerName", query);

    debouncedFetchCustomers(query);

    // Show dropdown when user starts typing
    if (query.length >= 3) {
      setShowCustomerDropdown(true);
    } else {
      setShowCustomerDropdown(false);
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleCustomerSearchChange",
      {
        elapsed_ms: "124",
        title: "Wo Detail Dialog",
        componentName: "WorkOrderFormDialog.tsx",
        version: packageJson.version,
      },
    );
  };

  const [customerDetailDialogOpen, setCustomerDetailDialogOpen] =
    useState(false);

  const handleCustomerDetailDialogOpen = () => {
    setCustomerDetailDialogOpen(true);
  };

  const handleCustomerDetailDialogClose = () => {
    setCustomerDetailDialogOpen(false);
  };

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  const warrantyDateRef = useRef<HTMLInputElement>(null);
  const pickupDateRef = useRef<HTMLInputElement>(null);
  const deliveryDateRef = useRef<HTMLInputElement>(null);
  const selectMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 240,
      },
    },
  };
  const handleNewCustomerDialogOpen = () => {
    setNewCustomerDialogOpen(true);
  };
  const handleNewCustomerDialogClose = () => {
    setNewCustomerDialogOpen(false);
  };

  const tabPanelSx = (tabIndex: number) => ({
    gridRow: 1,
    gridColumn: 1,
    visibility: activeTab === tabIndex ? "visible" : "hidden",
    pointerEvents: activeTab === tabIndex ? "auto" : "none",
    overflowY: activeTab === tabIndex ? "auto" : "hidden",
    minHeight: 0,
  });

  return (
    <Box
      mt={2}
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Tabs */}
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2, flexShrink: 0 }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTab-root": {
              color: theme.palette.text.secondary,
              "&.Mui-selected": {
                color: theme.palette.primary.main,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <Tab sx={{ textTransform: "none" }} label="Basic Details" />
          <Tab sx={{ textTransform: "none" }} label="Unit Details" />
          <Tab sx={{ textTransform: "none" }} label="Shipping Details" />
        </Tabs>
      </Box>

      {/* Tab Content - Scrollable; grid overlay keeps dialog size across tabs */}
      <Box
        className="wo-dialog-form-scroll"
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "1fr",
        }}
      >
        <Box sx={tabPanelSx(0)}>
          <Grid
            container
            spacing={{ xs: 1.5, lg: 2 }}
            sx={{ pb: 2 }}
          >
            {/* Customer */}
            <Grid item xs={formik.values.customerIdentity ? 8 : 11}>
              {/* Customer */}
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                ref={customerDropdownRef}
              >
                <TextField
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Stack direction="row" spacing={1} alignItems="center">
                          {loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SearchIcon />
                          )}
                          <IconButton
                            onClick={handleCustomerDetailDialogOpen}
                            edge="end"
                            size="small"
                          >
                            <InfoIcon
                              sx={(theme) => ({
                                color: theme.palette.text.secondary, // default color
                                cursor: "pointer",
                                transition: "color 0.3s ease",
                                "&:hover": {
                                  color: theme.palette.primary.main, // color on hover
                                },
                                // color on
                              })}
                            />
                          </IconButton>
                        </Stack>
                      </InputAdornment>
                    ),
                  }}
                  label={
                    <span>
                      Customer <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  sx={{
                    minWidth: "10%",
                    mt: 2,
                    "& .MuiInputBase-input::placeholder": {
                      textAlign: "left",
                    },
                  }}
                  variant="outlined"
                  fullWidth
                  name="customerIdentity"
                  size="small"
                  error={
                    !!(
                      formik.touched.customerIdentity &&
                      formik.errors.customerIdentity
                    )
                  }
                  //value={customerSearchTerm}
                  value={formik.values.customerName || ""}
                  onChange={(event) =>
                    handleCustomerSearchChange(event, formik)
                  }
                  onFocus={() => {
                    if (customers.length > 0) {
                      setShowCustomerDropdown(true);
                    }
                  }}
                  placeholder="Search"
                />
                {showCustomerDropdown && customers.length > 0 && (
                  <List sx={searchCustomerListStyle(theme)}>
                    {customers.map((customer) => (
                      <ListItem
                        sx={searchListItem(theme)}
                        key={customer.identity}
                        onClick={() => handleCustomerSelect(customer, formik)}
                      >
                        {customer.name} / {customer.number}
                      </ListItem>
                    ))}
                  </List>
                )}
              </FormControl>
            </Grid>

            {/* Add new Customer icon here */}
            <Grid
              item
              xs={1}
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconButton onClick={handleNewCustomerDialogOpen} size="small">
                <AddCircleIcon
                  sx={(theme) => ({
                    color: theme.palette.text.secondary, // default color
                    cursor: "pointer",
                    transition: "color 0.3s ease",
                    "&:hover": {
                      color: theme.palette.primary.main, // color on hover
                    },
                    // color on
                  })}
                />
              </IconButton>
            </Grid>

            {formik.values.customerIdentity && (
              <Grid item xs={3}>
                <FormControl sx={{ mt: 2 }} fullWidth disabled>
                  <InputLabel id="yes-no-label">Credit Hold</InputLabel>
                  <Select
                    size="small"
                    name="creditHold"
                    labelId="yes-no-label"
                    value={formik.values.creditHold || "false"}
                    label="Credit Hold"
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Reference -- status */}
            <Grid item xs={6}>
              {/* Reference */}
              <FormControl fullWidth size="small" variant="outlined">
                <TextField
                  label="My Reference"
                  value={formik.values.woDisplayText || ""}
                  onChange={formik.handleChange}
                  name="woDisplayText"
                  placeholder={"My Reference"}
                  variant="outlined"
                  fullWidth
                  size="small"
                  multiline
                  inputProps={{ maxLength: 15 }}
                  rows={1}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              {/* status */}
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: "10%" }}
              >
                <InputLabel
                  sx={{
                    color:
                      formik.touched.woStatusIdentity &&
                      formik.errors.woStatusIdentity
                        ? "#d32f2f"
                        : "inherit",
                  }}
                >
                  Status
                </InputLabel>
                <Select
                  value={formik.values.woStatusIdentity ?? ""}
                  onChange={formik.handleChange}
                  name="woStatusIdentity"
                  error={
                    !!(
                      formik.touched.woStatusIdentity &&
                      formik.errors.woStatusIdentity
                    )
                  }
                  label="status"
                  MenuProps={selectMenuProps}
                >
                  {woStatuses.map((status) => (
                    <MenuItem key={status.identity} value={status.identity}>
                      {status.displayText}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Work Order Description */}
            <Grid item xs={12}>
              <TextField
                label="Work Order Description"
                value={formik.values.woDescription || ""}
                onChange={formik.handleChange}
                name="woDescription"
                placeholder={"Work Order Description"}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                inputProps={{ maxLength: 50 }}
                rows={1}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: "10%" }}
              >
                <Autocomplete
                  options={locations}
                  size="small"
                  getOptionLabel={(option: SettingsLocation) =>
                    option.displayText
                  }
                  value={
                    locations.find(
                      (location) =>
                        location.identity === formik.values.locationIdentity,
                    ) || null
                  }
                  id="locationIdentity"
                  onChange={(event, newValue) => {
                    setLocationFormikFieldValue(
                      "locationIdentity",
                      newValue?.identity || null,
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      name="locationIdentity"
                      error={
                        !!(
                          formik.touched.locationIdentity &&
                          formik.errors.locationIdentity
                        )
                      }
                      {...params}
                      // label="Location*"
                      label={
                        <span>
                          Location <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            {/* Salesperson */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: "10%" }}
              >
                <Autocomplete
                  options={salesPersons}
                  size="small"
                  getOptionLabel={(option: ApplicationUser) => option.userName}
                  ListboxProps={{
                    sx: { maxHeight: 240 },
                  }}
                  value={
                    salesPersons.find(
                      (location) =>
                        location.identity === formik.values.salesPersonIdentity,
                    ) || null
                  }
                  id="salesPersonIdentity"
                  onChange={(event, newValue) => {
                    setSalesPersonFormikFieldValue(
                      newValue?.identity || null,
                      newValue?.userName || null,
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      error={
                        !!(
                          formik.touched.salesPersonIdentity &&
                          formik.errors.salesPersonIdentity
                        )
                      }
                      {...params}
                      // label="Sales Person*"
                      label={
                        <span>
                          Sales Person <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            {/* Technician */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: "10%" }}
              >
                <Autocomplete
                  options={technicians}
                  size="small"
                  getOptionLabel={(option: ApplicationUser) => option.userName}
                  ListboxProps={{
                    sx: { maxHeight: 240 },
                  }}
                  value={
                    technicians.find(
                      (location) =>
                        location.identity === formik.values.technicianIdentity,
                    ) || null
                  }
                  id="technicianIdentity"
                  onChange={(event, newValue) => {
                    setTechFormikFieldValue(
                      "technicianIdentity",
                      newValue?.identity || null,
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      error={
                        !!(
                          formik.touched.technicianIdentity &&
                          formik.errors.technicianIdentity
                        )
                      }
                      {...params}
                      label="Assigned Tech"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            {/* WO Priority */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: "10%" }}
              >
                <InputLabel>
                  <span>
                    WO Priority <span style={{ color: "red" }}>*</span>
                  </span>
                </InputLabel>
                <Select
                  value={formik.values.woPriorityIdentity ?? ""}
                  onChange={(event) => handleWoPriorityChange(event, formik)}
                  name="woPriorityIdentity"
                  error={
                    !!(
                      formik.touched.woPriorityIdentity &&
                      formik.errors.woPriorityIdentity
                    )
                  }
                  // label="WO Priority*"
                  label={
                    <span>
                      WO Priority <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                >
                  {woPriorityList.map((priority) => (
                    <MenuItem key={priority.identity} value={priority.identity}>
                      {priority.displayText}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* PO Number */}
            <Grid item xs={6}>
              <TextField
                label="PO No"
                fullWidth
                variant="outlined"
                value={formik.values?.purchaseOrderNumber ?? ""}
                onChange={formik.handleChange}
                name="purchaseOrderNumber"
                size="small"
                inputProps={{ maxLength: 50 }}
                rows={1}
              />
            </Grid>

            {/* Warranty Date */}
            <Grid item xs={6}>
              <TextField
                inputRef={warrantyDateRef}
                label="Warranty Date"
                name="warrantyDate"
                type="date"
                value={
                  formik.values.warrantyDate
                    ? formatDate2(formik.values.warrantyDate)
                    : ""
                }
                onChange={handleDateChange}
                error={
                  !!(formik.touched.warrantyDate && formik.errors.warrantyDate)
                }
                helperText={
                  formik.touched.warrantyDate && formik.errors.warrantyDate
                }
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "input[type='date']::-webkit-calendar-picker-indicator": {
                    cursor: "default",
                    color: theme.palette.text.primary,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "white" : "none",
                  },
                }}
              />
            </Grid>

            {/* Non-Printable Comments */}
            <Grid item xs={12}>
              <Box sx={{ position: "relative" }}>
                <Box
                  component="label"
                  sx={{
                    position: "absolute",
                    top: "-8px",
                    left: "14px",
                    backgroundColor: "background.paper",
                    padding: "0 4px",
                    fontSize: "0.75rem",
                    color:
                      formik.touched.nonPrintedComment &&
                      formik.errors.nonPrintedComment
                        ? "#d32f2f"
                        : "text.secondary",
                    zIndex: 1,
                  }}
                >
                  Non-Printed Comment
                </Box>
                <textarea
                  name="nonPrintedComment"
                  value={formik.values.nonPrintedComment ?? ""}
                  onChange={formik.handleChange}
                  maxLength={4000}
                  style={{
                    width: "100%",
                    minHeight: "120px",
                    padding: "16.5px 14px",
                    border:
                      formik.touched.nonPrintedComment &&
                      formik.errors.nonPrintedComment
                        ? "1px solid #d32f2f"
                        : "1px solid rgba(0, 0, 0, 0.23)",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                    fontSize: "0.875rem",
                    resize: "vertical",
                    outline: "none",
                    backgroundColor: "transparent",
                  }}
                  onFocus={(e) => {
                    e.target.style.border = "2px solid #1976d2";
                  }}
                  onBlur={(e) => {
                    e.target.style.border =
                      formik.touched.nonPrintedComment &&
                      formik.errors.nonPrintedComment
                        ? "1px solid #d32f2f"
                        : "1px solid rgba(0, 0, 0, 0.23)";
                  }}
                />
                {formik.touched.nonPrintedComment &&
                  formik.errors.nonPrintedComment && (
                    <Box
                      sx={{
                        color: "#d32f2f",
                        fontSize: "0.75rem",
                        mt: 0.5,
                        ml: 1.5,
                      }}
                    >
                      {formik.errors.nonPrintedComment}
                    </Box>
                  )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Unit Details Tab */}
        <Box sx={tabPanelSx(1)}>
          <Grid container spacing={1.5} sx={{ pb: 2, mt: "3px" }}>
            <Grid item xs={12}>
              <div ref={unitDropdownRef}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        label="Unit Number"
                        inputRef={unitNumberSearchRef}
                        variant="outlined"
                        fullWidth
                        name="stockNumber"
                        size="small"
                        value={formik.values.stockNumber ?? ""}
                        onChange={(event) =>
                          handleUnitSearchChange(event, formik, "unitNumber")
                        }
                        onFocus={() => {
                          handleUnitSearchOnFocus(formik, "unitNumber");
                          if (units.length > 0) {
                            setShowUnitDropdown(true);
                          }
                        }}
                        placeholder="Search by unit number"
                        InputProps={{
                          endAdornment:
                            unitSearchLoading &&
                            activeUnitSearchField === "unitNumber" ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SearchIcon />
                            ),
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: "10%" }}
                      />
                      {renderUnitSearchDropdown("unitNumber")}
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        label="Serial Number"
                        inputRef={serialSearchRef}
                        variant="outlined"
                        fullWidth
                        name="woUnitBaseSerial"
                        size="small"
                        value={formik.values.woUnitBaseSerial ?? ""}
                        onChange={(event) =>
                          handleUnitSearchChange(event, formik, "serial")
                        }
                        onFocus={() => {
                          handleUnitSearchOnFocus(formik, "serial");
                          if (units.length > 0) {
                            setShowUnitDropdown(true);
                          }
                        }}
                        placeholder="Search by serial"
                        InputProps={{
                          endAdornment:
                            unitSearchLoading &&
                            activeUnitSearchField === "serial" ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SearchIcon />
                            ),
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                      {renderUnitSearchDropdown("serial")}
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        label="Model"
                        inputRef={modelSearchRef}
                        variant="outlined"
                        fullWidth
                        name="model"
                        size="small"
                        value={formik.values.model ?? ""}
                        onChange={(event) =>
                          handleUnitSearchChange(event, formik, "model")
                        }
                        onFocus={() => {
                          handleUnitSearchOnFocus(formik, "model");
                          if (units.length > 0) {
                            setShowUnitDropdown(true);
                          }
                        }}
                        error={!!(formik.touched.model && formik.errors.model)}
                        placeholder="Search by model"
                        InputProps={{
                          endAdornment:
                            unitSearchLoading &&
                            activeUnitSearchField === "model" ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SearchIcon />
                            ),
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                      {renderUnitSearchDropdown("model")}
                    </Box>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            {/* Meter Type */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: "10%" }}
              >
                <InputLabel>Meter Type</InputLabel>
                <Select
                  value={formik.values.meterType ?? ""}
                  onChange={formik.handleChange}
                  name="meterType"
                  error={
                    !!(formik.touched.meterType && formik.errors.meterType)
                  }
                  label="Meter Type"
                >
                  {meterTypes.map((meter) => (
                    <MenuItem key={meter.key} value={meter.key}>
                      {meter.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Meter Value */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{ minWidth: "10%" }}
              >
                <TextField
                  size="small"
                  value={localMeterValue}
                  onChange={(e) => {
                    //
                    // const value =
                    //   e.target.value === "" ? 0 : Number(e.target.value);
                    // formik.setFieldValue("meterValue", value);
                    const value = e.target.value;

                    // Allow only valid decimal numbers or empty
                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                      setLocalMeterValue(value);

                      // If value is empty, treat as 0, else parseFloat
                      formik.setFieldValue(
                        "meterValue",
                        value === "" ? 0 : parseFloat(value),
                      );
                    }
                  }}
                  onFocus={() => {
                    // On focus, clear "0" to allow immediate typing
                    if (localMeterValue === "0") {
                      setLocalMeterValue("");
                    }
                  }}
                  name="meterValue"
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    // On blur, if empty, set to "0"
                    if (localMeterValue === "") {
                      setLocalMeterValue("0");
                      formik.setFieldValue("meterValue", 0);
                    }
                  }}
                  error={
                    formik.touched.meterValue && !!formik.errors.meterValue
                  }
                  helperText={
                    formik.touched.meterValue && formik.errors.meterValue
                  }
                  label="Meter Value"
                  variant="outlined"
                  fullWidth
                  // type="number"
                  placeholder="Meter Value"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    inputMode: "decimal", // mobile-friendly numeric keyboard
                    pattern: "[0-9]*\\.?[0-9]*", // soft pattern enforcement
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Shipping Tab */}
        <Box sx={tabPanelSx(2)}>
          <Box sx={{ pb: 2, mt: "3px" }}>
            {/* Section 1: Shipping */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.contrastText,
                  bgcolor: theme.palette.primary.main,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  mb: 1.5,
                }}
              >
                Shipping
              </Typography>
              <Grid container spacing={1.5}>
                {/* Pickup Date */}
                <Grid item xs={6}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                    <TextField
                      inputRef={pickupDateRef}
                      label="Pickup Date"
                      name="woPickupDate"
                      type="date"
                      value={
                        formik.values.woPickupDate
                          ? formatDate2(formik.values.woPickupDate)
                          : ""
                      }
                      onChange={handleDateChange}
                      error={
                        !!(
                          formik.touched.woPickupDate &&
                          formik.errors.woPickupDate
                        )
                      }
                      helperText={
                        formik.touched.woPickupDate && formik.errors.woPickupDate
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        flex: 1,
                        "input[type='date']::-webkit-calendar-picker-indicator": {
                          cursor: "default",
                          color: theme.palette.text.primary,
                          backgroundColor:
                            theme.palette.mode === "dark" ? "white" : "none",
                        },
                      }}
                    />
                    
                  </Box>
                </Grid>
                {/* Delivery Date */}
                <Grid item xs={6}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                    <TextField
                      inputRef={deliveryDateRef}
                      label="Delivery Date"
                      name="woDeliveryDate"
                      type="date"
                      value={
                        formik.values.woDeliveryDate
                          ? formatDate2(formik.values.woDeliveryDate)
                          : ""
                      }
                      onChange={handleDateChange}
                      error={
                        !!(
                          formik.touched.woDeliveryDate &&
                          formik.errors.woDeliveryDate
                        )
                      }
                      helperText={
                        formik.touched.woDeliveryDate &&
                        formik.errors.woDeliveryDate
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        flex: 1,
                        "input[type='date']::-webkit-calendar-picker-indicator": {
                          cursor: "default",
                          color: theme.palette.text.primary,
                          backgroundColor:
                            theme.palette.mode === "dark" ? "white" : "none",
                        },
                      }}
                    />
                    
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Section 2: Technician Calendar */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.contrastText,
                  bgcolor: theme.palette.primary.main,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  mb: 1.5,
                }}
              >
                Service Calendar V2
              </Typography>
              <Button
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                onClick={() => {
                  const invoiceNumber = formik.values.invoiceNumber;
                  const locationId = formik.values.locationIdentity;
                  const params = new URLSearchParams();
                  if (locationId) {
                    params.set("locationId", locationId.toString());
                  }
                  if (invoiceNumber) {
                    params.set("search", invoiceNumber);
                  }
                  const queryString = params.toString();
                  const url = queryString
                    ? `/technicianCalendar?${queryString}`
                    : "/technicianCalendar";
                  navigate(url);
                }}
                fullWidth
                size="medium"
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  py: 1,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                Open Service Calendar V2
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Customer details dialog */}
      {formik.values.customerIdentity && (
        <CustomerDetailDialog
          open={customerDetailDialogOpen}
          onClose={handleCustomerDetailDialogClose}
          customerPhone={formik.values.customerPhone || ""}
          customerEmail={formik.values.customerEmail || ""}
          customerAddress={formik.values.customerAddress || ""}
        />
      )}
      {newCustomerDialogOpen && (
        <NewCustomerDialog
          open={newCustomerDialogOpen}
          onClose={handleNewCustomerDialogClose}
          locationIdentity={formik.values.locationIdentity}
          entBy={user?.userName ?? ""}
        />
      )}
    </Box>
  );
};

export default WorkOrderDialog;
